// src/components/AsteroidMinter.js
import React, { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { createNft, mplTokenMetadata } from '@metaplex-foundation/mpl-token-metadata';
import { mplCore } from '@metaplex-foundation/mpl-core';
import {
    signerIdentity,
    generateSigner,
    percentAmount,
    createGenericFile,
} from '@metaplex-foundation/umi';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import { bundlrUploader } from '@metaplex-foundation/umi-uploader-bundlr';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
import {Connection, LAMPORTS_PER_SOL} from "@solana/web3.js";

// Accepts asteroids, outlineColor, bgColor to embed as NFT metadata attributes
export default function AsteroidMinter({ date, asteroids, outlineColor, bgColor }) {
    const wallet = useWallet();
    const [status, setStatus] = useState('');
    const [minted, setMinted] = useState(false);
    const [txUrl, setTxUrl] = useState('');

    const handleMint = async () => {
        if (!wallet.connected || !wallet.publicKey) {
            alert('Please connect your wallet first.');
            return;
        }
        try {
            setStatus('Capturing visualization…');
            const canvas = document.querySelector('canvas');
            if (!canvas) throw new Error('Canvas not found');

            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            const arrayBuffer = await blob.arrayBuffer();

            // Initialize UMI
            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT)
                .use(signerIdentity(wallet))
                .use(mplCore())
                .use(mplTokenMetadata())
                .use(bundlrUploader('https://node1.bundlr.network'))
                .use(walletAdapterIdentity(wallet));

            setStatus('Uploading image…');
            const imageFile = createGenericFile(arrayBuffer, `astro-${date}.png`, {
                tags: [{ name: 'Content-Type', value: 'image/png' }],
            });
            const [imageUri] = await umi.uploader.upload([imageFile]);

            setStatus('Preparing metadata…');
            // Build metadata including user selections and asteroid attributes
            const metadataObj = {
                name: `AstroData-${date}`,
                description: `Visualization of near-Earth asteroids for ${date}.`,
                image: imageUri,
                date,
                attributes: [
                    { trait_type: 'background', value: '#' + bgColor },
                    { trait_type: 'outline',    value: outlineColor },
                    ...asteroids.map(a => ({ trait_type: a.name, value: a.color }))
                ],
                properties: {
                    files: [{ uri: imageUri, type: 'image/png' }],
                    category: 'image',
                },
            };

            setStatus('Uploading metadata…');
            const metadataUri = await umi.uploader.uploadJson(metadataObj);

            setStatus('Minting NFT…');
            const mint = generateSigner(umi);
            await createNft(umi, {
                mint,
                name: metadataObj.name,
                uri: metadataUri,
                sellerFeeBasisPoints: percentAmount(5.0),
            }).sendAndConfirm(umi);

            const url = `https://explorer.solana.com/address/${mint.publicKey.toString()}?cluster=mainnet-beta`;
            setTxUrl(url);
            setMinted(true);
            setStatus('🎉 Mint successful!');
        } catch (err) {
            console.error(err);
            setStatus(`Error: ${err.message}`);
        }
    };

    return (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            {!minted && <button onClick={handleMint}>Mint Visualization</button>}
            {status && <p style={{ whiteSpace: 'pre-wrap', marginTop: '1rem' }}>{status}</p>}
            {minted && txUrl && (
                <p style={{ marginTop: '0.5rem' }}>
                    <a href={txUrl} target="_blank" rel="noopener noreferrer">
                        View minted NFT on Explorer
                    </a>
                </p>
            )}
        </div>
    );
}
